import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Download, Film, Loader2, Sparkles, XCircle } from "lucide-react";

const STATUS_META = {
  pending: { label: "En attente", cls: "bg-[#8B5CF6]/15 text-[#8B5CF6]", icon: Loader2 },
  processing: { label: "En cours", cls: "bg-[#8B5CF6]/15 text-[#8B5CF6]", icon: Loader2 },
  completed: { label: "Terminée", cls: "bg-emerald-500/15 text-emerald-400", icon: Sparkles },
  failed: { label: "Échouée", cls: "bg-red-500/15 text-red-400", icon: XCircle },
};

const VideoCard = ({ v }) => {
  const meta = STATUS_META[v.status] || STATUS_META.pending;
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`myvideo-card-${v.id}`}>
      <div className="relative aspect-video bg-black/50">
        {v.status === "completed" ? (
          <video src={`${process.env.REACT_APP_BACKEND_URL}/api/videos/${v.id}/file`} controls preload="metadata" className="w-full h-full" data-testid="myvideo-player" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
            <meta.icon className={`w-8 h-8 ${v.status !== "failed" ? "animate-spin" : ""}`} />
            <span className="mt-2 text-xs">{meta.label}</span>
          </div>
        )}
        <span className={`absolute top-3 left-3 text-xs font-medium rounded-full px-2.5 py-1 ${meta.cls}`} data-testid={`myvideo-status-${v.id}`}>{meta.label}</span>
      </div>
      <div className="p-4">
        <p className="text-sm line-clamp-2">{v.prompt}</p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{v.model} · {v.aspect_ratio}</span>
          {v.status === "completed" && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/videos/${v.id}/file`}
              download
              className="flex items-center gap-1 text-xs text-[#EC4899] hover:underline"
              data-testid={`myvideo-download-${v.id}`}
            >
              <Download className="w-3.5 h-3.5" /> Télécharger
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const MyVideos = () => {
  const { user } = useAuth();
  const [videos, setVideos] = useState(null);

  useEffect(() => {
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => setVideos([]));
  }, []);

  return (
    <div className="noise relative min-h-[80vh]">
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl sm:text-4xl font-black" data-testid="myvideos-title">
            Mes <span className="text-gradient">vidéos</span>
          </h1>
          <span className="text-sm text-muted-foreground">Plan : {user?.plan === "free" ? "Gratuit" : user?.plan} · {user?.credits} crédits</span>
        </div>

        {videos === null ? (
          <div className="mt-16 text-center text-muted-foreground">Chargement…</div>
        ) : videos.length === 0 ? (
          <div className="mt-16 rounded-3xl border border-dashed border-border py-20 text-center" data-testid="myvideos-empty">
            <Film className="w-10 h-10 mx-auto text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">Aucune vidéo pour l'instant.</p>
            <Link to="/studio" className="inline-block mt-4 text-[#8B5CF6] hover:underline" data-testid="myvideos-empty-cta">Créer ma première vidéo →</Link>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((v) => <VideoCard key={v.id} v={v} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyVideos;
